import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { SourceEvolutionPoint } from '@/hooks/useSuperStats';

interface SourceEvolutionProps {
  data: SourceEvolutionPoint[];
  loading: boolean;
}

// Palette issue des rôles StyleSystem (aucune couleur codée en dur).
const COLORS = [
  'oklch(var(--primary-500))',
  'oklch(var(--accent-500))',
  'oklch(var(--secondary-500))',
  'oklch(var(--primary-300))',
  'oklch(var(--accent-300))',
  'oklch(var(--foreground-400))',
];

/**
 * Courbe « évolution des sources » : nombre de visites par provenance
 * (Google, WhatsApp, Instagram…) jour après jour sur la période analysée.
 * Permet de voir concrètement quand un nouveau canal (ex. Google, WhatsApp)
 * commence à générer du trafic.
 */
export default function SourceEvolution({ data, loading }: SourceEvolutionProps) {
  const columns = data.length > 0
    ? Object.keys(data[0]).filter((k) => k !== 'date' && k !== 'total')
    : [];
  const total = data.reduce((sum, point) => sum + point.total, 0);
  const hasData = total > 0;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 mb-5 md:mb-6">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-1">
        <i className="ri-flow-chart text-primary-600"></i>
        Évolution des sources dans le temps
      </h3>
      <p className="text-xs text-foreground-500 mb-4">
        Arrivée de chaque provenance (Google, WhatsApp, réseaux sociaux…) jour après jour sur la période.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-56">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center h-56 text-center">
          <i className="ri-flow-chart text-3xl text-foreground-300 mb-2"></i>
          <p className="text-sm text-foreground-400">
            Aucune source de trafic enregistrée sur cette période.
          </p>
          <p className="text-xs text-foreground-400 mt-1">
            Les nouvelles visites (Google, WhatsApp…) apparaîtront ici au fil de l&apos;eau.
          </p>
        </div>
      ) : (
        <div className="w-full h-64 md:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-200))" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }}
                axisLine={{ stroke: 'oklch(var(--background-200))' }}
                tickLine={false}
                minTickGap={16}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: 'oklch(var(--foreground-500))' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid oklch(var(--background-200))',
                  fontSize: 12,
                }}
                labelStyle={{ color: 'oklch(var(--foreground-700))', fontWeight: 600 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value: string) => (
                  <span className="text-foreground-600">{value}</span>
                )}
              />
              {columns.map((name, i) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={name}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}